import { humanizeKey } from "utils/humanizeKey";

/**
 * Shared helpers for walking (Pydantic) JSON Schemas. The schema-driven form
 * (JsonSchemaForm) and the pending-changes diff (pending/configDiff) must
 * resolve nodes and derive labels the same way, so the logic lives here.
 */

export type Schema = Record<string, any>;

/**
 * Resolve a ``$ref`` node against the root schema ($defs). Single-level by
 * design: callers recurse through VALUES, not the schema, so cyclic $refs
 * cannot loop. Property-level siblings win over the $defs entry (Pydantic
 * emits description/title/default NEXT TO the $ref).
 */
export function deref(node: Schema, root: Schema): Schema {
  if (node && typeof node.$ref === "string") {
    const path = node.$ref.replace(/^#\//, "").split("/");
    let resolved: any = root;
    for (const part of path) resolved = resolved?.[part];
    if (!resolved) return node;
    const { $ref: _ref, ...siblings } = node;
    return { ...resolved, ...siblings };
  }
  return node;
}

/** Unwrap ``anyOf: [T, null]`` (Pydantic Optional) to its non-null branch. */
export function unwrapOptional(node: Schema): Schema {
  if (Array.isArray(node.anyOf)) {
    const nonNull = node.anyOf.find((s: Schema) => s?.type !== "null");
    if (nonNull) {
      return {
        ...nonNull,
        title: node.title ?? nonNull.title,
        description: node.description ?? nonNull.description,
        default: node.default !== undefined ? node.default : nonNull.default,
      };
    }
  }
  return node;
}

export function enumValues(node: Schema, root: Schema): string[] | null {
  const resolved = deref(node, root);
  if (Array.isArray(resolved.enum)) return resolved.enum.map(String);
  return null;
}

/**
 * Fully resolve a property/items node: deref, unwrap Optional, then deref
 * again for the ``anyOf: [$ref, null]`` shape (Optional nested model/enum).
 */
export function resolveNode(node: Schema, root: Schema): Schema {
  let out = unwrapOptional(deref(node, root));
  if (typeof out.$ref === "string") out = deref(out, root);
  return out;
}

/**
 * Human label for an object property. Prefers the PROPERTY-level title
 * (Pydantic auto-titles plain fields from the field name; explicit
 * ``Field(title=...)`` also lands there) and falls back to the humanized key.
 * Deliberately ignores titles that only exist on the dereferenced $defs
 * entry - those carry the nested model/enum CLASS name (e.g.
 * "FilenameMatchConfig"), not the field's name.
 */
export function propertyLabel(
  key: string,
  rawProp: Schema | undefined,
): string {
  const title =
    rawProp && typeof rawProp.title === "string" ? rawProp.title : undefined;
  return humanizeKey(title ?? key);
}

/** Property node of an object schema, raw (pre-deref) and resolved forms. */
export function resolveProperty(
  objSchema: Schema | null,
  root: Schema | null,
  key: string,
): { raw: Schema | undefined; resolved: Schema | null } {
  const raw: Schema | undefined = objSchema?.properties?.[key];
  if (!raw) return { raw: undefined, resolved: null };
  return { raw, resolved: root ? resolveNode(raw, root) : null };
}

/** Resolved ``items`` schema of an array node, or null when unknown. */
export function resolveItems(
  arraySchema: Schema | null,
  root: Schema | null,
): Schema | null {
  const items = arraySchema?.items;
  if (!items || !root) return null;
  return resolveNode(items, root);
}
